import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import { useFormConfigStore } from "../../stores/form-config-store";
import type { ApiPage, Brandkit, FormBrand } from "../../types/api.types";

interface StepWelcomeProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	isDarkMode?: boolean;
}

function getFormatDescription(formats: string[]): string {
	const hasText = formats.includes("text");
	const hasVideo = formats.includes("video");
	const hasAudio = formats.includes("audio");
	const hasAny = formats.includes("any");

	if (hasAny || (hasText && hasVideo && hasAudio))
		return "Text, audio, or video—your choice";
	if (hasText && hasVideo) return "Text or video—your choice";
	if (hasText && hasAudio) return "Text or audio—your choice";
	if (hasVideo && hasAudio) return "Video or audio—your choice";
	if (hasVideo) return "Video response";
	if (hasAudio) return "Audio response";
	return "Text response";
}

export default function StepWelcome({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig,
	isDarkMode,
}: StepWelcomeProps) {
	const { nextStep } = useFeedbackFlow();
	const { getQuestions, getPages } = useFormConfigStore();
	const questions = getQuestions();
	const pages = getPages();
	const questionCount = questions.length;
	const formatDescription = getFormatDescription(
		questions.map((q) => q.format),
	);
	const isCompanyStepEnabled = pages.some(
		(p) => p.type === "about_company" && p.status === "enabled",
	);

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<BoldLayout
			showBranding={brand?.branding !== "disabled"}
			brandName={brand.name}
			brandLogo={brandLogo}
			brandkit={brandkit}
			stepKey={stepKey}
			showBackButton={showBackButton}
			onBack={onBack}
			onContinue={nextStep}
			continueLabel="Continue"
			title={pageConfig.attributes.title}
			description={pageConfig.attributes.description}
			isDarkMode={isDarkMode}
		>
			<div className="space-y-3 text-left">
				{(() => {
					let stepNumber = 0;
					const steps = [];

					steps.push(
						<div
							key="questions"
							className="flex items-center gap-4 py-4 rounded-xl"
						>
							<div
								className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-t-on-primary shrink-0"
								style={{
									backgroundColor:
										"var(--color-accent, #e17100)",
								}}
							>
								{++stepNumber}
							</div>
							<div>
								<p
									className={`${isDarkMode ? "text-white" : "text-t-primary"}`}
									style={{
										fontFamily: "var(--font-body)",
										fontWeight: "var(--font-body-weight)",
									}}
								>
									Answer {questionCount} quick{" "}
									{questionCount === 1
										? "question"
										: "questions"}
								</p>
								<p
									className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
									style={{
										fontFamily: "var(--font-body)",
										fontWeight: "var(--font-body-weight)",
									}}
								>
									{formatDescription}
								</p>
							</div>
						</div>,
					);

					steps.push(
						<div
							key="details"
							className="flex items-center gap-4 py-4 rounded-xl"
						>
							<div
								className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-t-on-primary shrink-0"
								style={{
									backgroundColor:
										"var(--color-accent, #e17100)",
								}}
							>
								{++stepNumber}
							</div>
							<div>
								<p
									className={`font-medium ${isDarkMode ? "text-white" : "text-t-primary"}`}
									style={{
										fontFamily: "var(--font-body)",
										fontWeight: "var(--font-body-weight)",
									}}
								>
									Add your details
								</p>
								<p
									className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
									style={{
										fontFamily: "var(--font-body)",
										fontWeight: "var(--font-body-weight)",
									}}
								>
									Name, email, and optionally your photo
								</p>
							</div>
						</div>,
					);

					if (isCompanyStepEnabled) {
						steps.push(
							<div
								key="company"
								className="flex items-center gap-4 py-4 rounded-xl"
							>
								<div
									className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-t-on-primary shrink-0"
									style={{
										backgroundColor:
											"var(--color-accent, #e17100)",
									}}
								>
									{++stepNumber}
								</div>
								<div>
									<p
										className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-t-primary"}`}
										style={{
											fontFamily: "var(--font-body)",
											fontWeight:
												"var(--font-body-weight)",
										}}
									>
										<span>Add company details</span>
										<span>(optional)</span>
									</p>
									<p
										className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
										style={{
											fontFamily: "var(--font-body)",
											fontWeight:
												"var(--font-body-weight)",
										}}
									>
										Job title, company, website and logo
									</p>
								</div>
							</div>,
						);
					}

					steps.push(
						<div
							key="privacy"
							className="flex items-center gap-4 py-4 rounded-xl"
						>
							<div
								className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-t-on-primary shrink-0"
								style={{
									backgroundColor:
										"var(--color-accent, #e17100)",
								}}
							>
								{++stepNumber}
							</div>
							<div>
								<p
									className={`font-medium ${isDarkMode ? "text-white" : "text-t-primary"}`}
									style={{
										fontFamily: "var(--font-body)",
										fontWeight: "var(--font-body-weight)",
									}}
								>
									Choose your privacy
								</p>
								<p
									className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
									style={{
										fontFamily: "var(--font-body)",
										fontWeight: "var(--font-body-weight)",
									}}
								>
									You control where it's shared
								</p>
							</div>
						</div>,
					);

					return steps;
				})()}
			</div>
		</BoldLayout>
	);
}
